import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Target, AlertTriangle, Calendar, Star, Activity, RefreshCw, Zap, Plus } from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { formatCurrency } from '../utils/helpers';
import toast from 'react-hot-toast';
import { analysisService, recommendationService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import StrategyModal from '../components/strategyModal';

const AIRecommendationsDashboard = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [currentPortfolio, setCurrentPortfolio] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('all');
  const [isRebalancing, setIsRebalancing] = useState(false);
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const navigate = useNavigate();
  const [availableSectors, setAvailableSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // ✨ 새로 추가: 섹터 목록 로드
  useEffect(() => {
    const loadSectors = async () => {
      try {
        const response = await recommendationService.getAvailableSectors();
        setAvailableSectors(response.data.sectors || []);
      } catch (error) {
        console.error('섹터 목록 로드 실패:', error);
      }
    };
    loadSectors();
  }, []);

  // 관심 종목 추가 페이지로 이동
  const handleAddUserStock = () => {
    navigate('/add-stock');
  };

  // 추천관리 페이지로 이용
  const openAImanager = () => {
    window.open('http://192.168.0.20:10009/portfolio/dashboard', '_blank', 'noopener,noreferrer');
  };

  // 우선순위 재평가
  const handleRebalancePortfolio = async () => {
    if (isRebalancing) return;
    
    const confirmed = window.confirm(
      'AI 추천 데이터를 기반으로 포트폴리오 우선순위를 재평가합니다.\n계속하시겠습니까?'
    );
    
    if (!confirmed) return;
    
    try {
      setIsRebalancing(true);
      toast.loading('포트폴리오 우선순위 재평가 중...', { id: 'rebalance' });
      
      const response = await analysisService.rebalancePortfolio();
      
      if (response.data.success) {
        toast.success(
          `✅ 재평가 완료! ${response.data.summary || '포트폴리오가 업데이트되었습니다.'}`, 
          { id: 'rebalance' }
        );
        
        await loadData();
      } else {
        toast.error(
          `⚠️ 재평가 실패: ${response.data.message || '알 수 없는 오류'}`, 
          { id: 'rebalance' }
        );
      }
      
    } catch (error) {
      console.error('재평가 실패:', error);
      toast.error(
        `❌ 재평가 오류: ${error.response?.data?.detail || error.message}`, 
        { id: 'rebalance' }
      );
    } finally {
      setIsRebalancing(false);
    }
  };

  // 수동 AI 추천 실행
  const handleManualRecommendation = async () => {
    if (isRunningAnalysis) return;
    
    const confirmed = window.confirm(
      'AI 추천 시스템을 수동으로 실행합니다.\n' +
      '현재 시장 상황을 분석하여 새로운 추천을 생성합니다.\n' +
      '실행하시겠습니까? (약 5-10분 소요)'
    );
    
    if (!confirmed) return;
    
    try {
      setIsRunningAnalysis(true);
      toast.loading('AI 추천 분석을 실행 중입니다... (5-10분 소요)', { id: 'manual-recommendation' });
      
      // ✅ 수정: runDailyAnalysis로 변경
      const response = await recommendationService.runDailyAnalysis();
      
      // ✅ 수정: 10009 응답 형식에 맞게 처리
      // 10009는 { status: "analysis_started", message: "...", estimated_time: "5-15분" } 형식
      if (response.data && response.status === 200) {
        const status = response.data.status || '';
        const message = response.data.message || '';
        
        // "analysis_started"이면 성공으로 간주
        // ✅ 응답 처리 수정
      if (response.data && response.status === 200) {
        const status = response.data.status || '';
        const message = response.data.message || '';
        
        if (status === 'analysis_started' || status === 'success') {
          toast.success(`✅ ${message}`, { id: 'manual-recommendation', duration: 5000 });
        } else {
          toast.success(`✅ AI 추천 완료! ${message}`, { id: 'manual-recommendation', duration: 5000 });
        } 
        } else {
          toast.success(
            `✅ AI 추천 완료! ${message || '새로운 추천이 생성되었습니다.'}`, 
            { id: 'manual-recommendation', duration: 5000 }
          );
        }

        // 5초 후 데이터 새로고침
        setTimeout(() => {
          loadData();
        }, 5000);
        
      } else {
        toast.error(
          `⚠️ AI 추천 실패: ${response.data?.message || '알 수 없는 오류'}`, 
          { id: 'manual-recommendation' }
        );
      }
      
    } catch (error) {
      console.error('수동 AI 추천 실패:', error);
      toast.error(
        `❌ AI 추천 오류: ${error.response?.data?.detail || error.message}`, 
        { id: 'manual-recommendation' }
      );
    } finally {
      setIsRunningAnalysis(false);
    }
  };

  // ✨ 새로 추가: 선택한 섹터 분석
  const handleSectorAnalysis = async () => {
    if (!selectedSector) {
      toast.error('섹터를 선택해주세요');
      return;
    }
    
    if (isRunningAnalysis) return;
    
    const confirmed = window.confirm(
      `"${selectedSector}" 섹터를 새로 분석합니다.\n` +
      `기존 추천에 추가로 분석 결과가 생성됩니다.\n` +
      `계속하시겠습니까? (5-15분 소요)`
    );
    
    if (!confirmed) return;
    
    try {
      setIsRunningAnalysis(true);
      toast.loading(`${selectedSector} 섹터 분석 중...`, { id: 'sector-analysis' });
      
      const response = await recommendationService.runSectorAnalysis(selectedSector);
      
      if (response.status === 200) {
        const message = response.data.message || `${selectedSector} 섹터 분석이 시작되었습니다.`;
        toast.success(`✅ ${message}`, { id: 'sector-analysis', duration: 5000 });
        
        setTimeout(() => loadData(), 5000);
      } else {
        toast.error(`⚠️ 분석 시작 실패`, { id: 'sector-analysis' });
      }
      
    } catch (error) {
      console.error('섹터 분석 실패:', error);
      toast.error(
        `❌ 분석 오류: ${error.response?.data?.detail || error.message}`, 
        { id: 'sector-analysis' }
      );
    } finally {
      setIsRunningAnalysis(false);
    }
  };

  // 데이터 로드
  const loadData = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 AI 추천 데이터 로드 시작...');
      
      const recommendationsResponse = await analysisService.getAIRecommendations();
      const portfolioResponse = await analysisService.getPortfolio();
      
      const rawRecommendations = recommendationsResponse?.data?.data || [];
      
      if (Array.isArray(rawRecommendations)) {
        const groupedByDate = {};
        
        rawRecommendations.forEach(rec => {
          try {
            // ✅ 수정: 날짜만 정확히 추출 (처음 10자리)
            const dateStr = rec.created_at 
              ? rec.created_at.substring(0, 10)
              : '2025-09-20';
            
            if (!groupedByDate[dateStr]) {
              groupedByDate[dateStr] = [];
            }
            
            groupedByDate[dateStr].push({
              stock_code: rec.stock_code || '',
              stock_name: rec.company_name || '',
              current_price: parseFloat(rec.current_price || 0),
              target_price: parseFloat(rec.target_price || 0),
              confidence: parseFloat(rec.confidence_score || 0),
              sector: rec.sector || '',
              action: 'BUY',
              reason: rec.analysis_summary || '추천 종목',
              expected_return: parseFloat(rec.expected_return_rate || 0)
            });
          } catch (err) {
            console.warn('추천 데이터 변환 오류:', err, rec);
          }
        });
        
        const formattedRecommendations = Object.keys(groupedByDate)
          .sort((a, b) => b.localeCompare(a))
          .map(date => ({
            date,
            stocks: groupedByDate[date]
          }));
        
        setRecommendations(formattedRecommendations);
      } else {
        setRecommendations([]);
      }
      
      const portfolioData = portfolioResponse?.data?.data || [];
      if (Array.isArray(portfolioData)) {
        setCurrentPortfolio(portfolioData);
      } else {
        setCurrentPortfolio([]);
      }
      
      toast.success('✅ AI 추천 데이터를 성공적으로 로드했습니다!');
    } catch (error) {
      console.error('❌ 데이터 로딩 오류:', error);
      toast.error(`데이터를 불러오는데 실패했습니다: ${error.message || '알 수 없는 오류'}`);
      setRecommendations([]);
      setCurrentPortfolio([]);
    } finally {
      setIsLoading(false);
    }
  }

  const getActionIcon = (action) => {
    switch (action) {
      case 'BUY':
        return <TrendingUp className="w-3 h-3 text-red-600" />;
      case 'SELL':
        return <TrendingDown className="w-3 h-3 text-blue-600" />;
      case 'HOLD':
        return <Target className="w-3 h-3 text-gray-600" />;
      default:
        return <Activity className="w-3 h-3 text-gray-400" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'BUY':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'SELL':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'HOLD':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getConfidenceColor = (confidence) => {
    const conf = parseFloat(confidence);
    if (conf >= 80) return 'text-green-600';
    if (conf >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSectorColor = (sector) => {
    const colors = {
      '반도체': 'bg-blue-100 text-blue-800',
      'AI': 'bg-purple-100 text-purple-800',
      '바이오': 'bg-green-100 text-green-800',
      '헬스케어': 'bg-green-100 text-green-800',
      '통신서비스': 'bg-orange-100 text-orange-800',
      '방위사업': 'bg-gray-100 text-gray-800',
      '양자컴퓨팅': 'bg-indigo-100 text-indigo-800',
      'default': 'bg-gray-100 text-gray-600'
    };
    return colors[sector] || colors.default;
  };

  const getDateOptions = () => {
    const uniqueDates = [...new Set(recommendations.map(rec => rec.date))].sort((a, b) => b.localeCompare(a));
    return uniqueDates;
  };

  const dateOptions = getDateOptions();
  
  const filteredRecommendations = selectedDate === 'all' 
    ? recommendations 
    : recommendations.filter(rec => rec.date === selectedDate);

  const totalStocksCount = filteredRecommendations.reduce((acc, rec) => {
    return acc + (rec.stocks ? rec.stocks.length : 0);
  }, 0);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <Loading message="AI 추천 현황을 불러오는 중..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 140px)' }}>
        {/* 전체 헤더 */}
        <div className="px-6 py-1 bg-white border-b flex items-center justify-between" style={{ minHeight: '44px', maxHeight: '44px' }}>
          <h1 className="text-xl font-bold text-gray-900">
            AI 투자 추천 현황
          </h1>
          {/* 관심 종목 추가 버튼 */}
          {/* <button
            onClick={handleAddUserStock}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm transition-all"
          >
            <Plus className="w-4 h-4 mr-1" />
            관심 종목 추가
          </button> */}
          <button
            onClick={openAImanager}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm transition-all"
          >
            <Plus className="w-4 h-4 mr-1" />
            Portfolio 관리(새창)
          </button>
        </div>

        {/* 좌우 분할 레이아웃 */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 overflow-hidden">
          
          {/* 🔵 좌측: 현재 AI 포트폴리오 */}
          <div className="lg:col-span-4 border-r bg-white h-full overflow-hidden">
            <div className="h-full flex flex-col">
              {/* 포트폴리오 헤더 - 높이 통일 */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-4 py-1 border-b flex items-center justify-between" style={{ minHeight: '40px', maxHeight: '40px' }}>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-yellow-600" />
                  <h2 className="font-bold text-gray-900 text-sm">현재 AI 포트폴리오</h2>
                  <span className="text-xs text-gray-600">({currentPortfolio.length})</span>
                </div>
                {/* 우선순위 재평가 버튼 */}
                <button
                  onClick={handleRebalancePortfolio}
                  disabled={isRebalancing}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg shadow-sm transition-all ${
                    isRebalancing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  {isRebalancing ? (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      재평가중...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1" />
                      우선순위 재평가
                    </>
                  )}
                </button>
              </div>
              
              {/* 포트폴리오 내용 - 스크롤 */}
              <div className="flex-1 overflow-y-auto p-2 portfolio-scroll">
                {currentPortfolio.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm">현재 활성화된 포트폴리오가 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {currentPortfolio.map((stock, index) => (
                      <div key={stock.symbol || index} className="bg-gray-50 rounded-lg p-3 border hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-bold text-gray-900 text-sm">{stock.name}</h3>
                            <p className="text-xs text-gray-500 font-mono">{stock.symbol}</p>
                          </div>
                          <div className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full border text-xs font-medium ${getActionColor(stock.action)}`}>
                            {getActionIcon(stock.action)}
                            <span className="text-xs">{stock.action}</span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">신뢰도</span>
                          <span className={`font-bold ${getConfidenceColor(stock.confidence)}`}>
                            {stock.confidence}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 🔵 우측: 최근 AI 추천 */}
          <div className="lg:col-span-8 bg-white h-full overflow-hidden">
            <div className="h-full flex flex-col">
              {/* 추천 헤더 - 높이 통일 */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-1 border-b flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <h2 className="font-bold text-gray-900 text-sm">최근 AI 추천</h2>
                  <span className="text-xs text-gray-600">({totalStocksCount})</span>
                  
                  {/* 날짜 필터 */}
                  {dateOptions.length > 0 && (
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="ml-2 border border-gray-300 rounded-lg px-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="all">모든 데이터</option>
                      {dateOptions.map(dateStr => (
                        <option key={dateStr} value={dateStr}>{dateStr}</option>
                      ))}
                    </select>
                  )}
                </div>
                
                {/* ✨ 새로운 버튼 영역 */}
                <div className="flex items-center space-x-2">
                  
                    <button onClick={() => setModalOpen(true)} style={{
                      padding: '6px 24px', borderRadius: '6px', background: '#333', color: '#fff', border: 0, fontSize: '13px'
                    }}>
                      선택 전략 보기
                    </button>

                    <StrategyModal open={modalOpen} onClose={() => setModalOpen(false)} />
                  
                  {/* 섹터 선택 드롭다운 */}
                  {/* <select
                    value={selectedSector}
                    onChange={(e) => setSelectedSector(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    disabled={isRunningAnalysis}
                  >
                    <option value="">섹터 선택</option>
                    {availableSectors.map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select> */}
                  
                  {/* 선택한 섹터 분석 버튼 */}
                  {/* <button
                    onClick={handleSectorAnalysis}
                    disabled={isRunningAnalysis || !selectedSector}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg shadow-sm transition-all ${
                      isRunningAnalysis || !selectedSector
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {isRunningAnalysis ? (
                      <>
                        <Zap className="w-3 h-3 mr-1 animate-pulse" />
                        분석중...
                      </>
                    ) : (
                      <>
                        <Zap className="w-3 h-3 mr-1" />
                        섹터 분석
                      </>
                    )}
                  </button> */}
                  
                  {/* 기존 당일 섹터 분석 버튼 */}
                  {/* <button
                    onClick={handleManualRecommendation}
                    disabled={isRunningAnalysis}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg shadow-sm transition-all ${
                      isRunningAnalysis
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                    title="오늘의 섹터 자동 분석"
                  >
                    {isRunningAnalysis ? (
                      <>
                        <Zap className="w-3 h-3 mr-1 animate-pulse" />
                        AI 분석중...
                      </>
                    ) : (
                      <>
                        <Zap className="w-3 h-3 mr-1" />
                        오늘 섹터
                      </>
                    )}
                  </button> */}
                  
                </div>
              </div>
              {/* 추천 내용 - 스크롤 */}
              <div className="flex-1 overflow-y-auto p-2 recommendations-scroll">
                {filteredRecommendations.length === 0 ? (
                  <div className="text-center py-20">
                    <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">선택한 기간에 추천 내역이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {filteredRecommendations.map((dayRec) => (
                      <div key={dayRec.date}>
                        {/* 날짜 구분선 */}
                        <div className="flex items-center mb-3">
                          <div className="bg-blue-500 text-white rounded px-3 py-1 text-sm font-bold">
                            {dayRec.date}
                          </div>
                          <div className="flex-1 h-px bg-gray-200 ml-3"></div>
                        </div>
                        
                        {/* 종목 카드 그리드 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                          {(dayRec.stocks || []).map((stock, stockIndex) => (
                            <div 
                              key={stock.stock_code || `stock-${stockIndex}`} 
                              className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-blue-400 transition-all"
                            >
                              {/* 헤더 */}
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-1 mb-1">
                                    <h4 className="font-bold text-gray-900 text-sm truncate">{stock.stock_name}</h4>
                                    <div className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getActionColor(stock.action)}`}>
                                      {getActionIcon(stock.action)}
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-500 font-mono">{stock.stock_code}</p>
                                </div>
                                <div className={`px-2 py-0.5 rounded text-xs font-medium ${getSectorColor(stock.sector)} ml-1`}>
                                  {stock.sector}
                                </div>
                              </div>
                              
                              {/* 가격 정보 */}
                              {stock.current_price > 0 && stock.target_price > 0 ? (
                                <div className="space-y-1.5 mb-2 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">현재가</span>
                                    <span className="font-bold text-gray-900">
                                      {formatCurrency(stock.current_price)}
                                    </span>
                                  </div>
                                  
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">목표가</span>
                                    <span className="font-bold text-green-600">
                                      {formatCurrency(stock.target_price)}
                                    </span>
                                  </div>
                                  
                                  <div className="flex justify-between pt-1 border-t border-gray-100">
                                    <span className="text-gray-600">기대수익</span>
                                    <span className="font-bold text-green-600">
                                      {stock.expected_return > 0 ? `+${stock.expected_return.toFixed(1)}%` : 'N/A'}
                                    </span>
                                  </div>
                                  
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">신뢰도</span>
                                    <span className={`font-bold ${getConfidenceColor(stock.confidence)}`}>
                                      {stock.confidence}%
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-2 text-gray-500 text-xs">
                                  가격 정보 없음
                                </div>
                              )}
                              
                              {/* 추천 사유 */}
                              <div className="bg-blue-50 rounded p-2 mt-2 cursor-help" title={stock.reason}>
                                <p className="text-xs text-gray-700 leading-relaxed line-clamp-2">{stock.reason}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIRecommendationsDashboard;
